import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DwButtonConfig, DwModalConfig, DwModalHandler, DwModalResult, DwModalSize, DwModalButtonStandardConfigs, DwModalService, DwOrmDataService, DwOrmDataServiceToken } from '@devwareapps/devware-cap';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppMetaData, ChallengeTeamMemberEntity, UserChallengeEntity, UserProfileEntity, UserFitnessSummaryEntity } from '../../../../meta-data/app-meta-data.service';
import { CentralDataService, CentralTeamChange } from '../../../streaker-central/services/central-data.service';
import { userProfilePath } from '../../services/profile-routing/profile-links';

export interface TeamMemberPickerData {
    teamId: number;
    challengeId: number;
    members: ChallengeTeamMemberEntity[];
    onChanged: (change: CentralTeamChange) => void;
}
export function openTeamMemberPicker(modal: DwModalService, data: TeamMemberPickerData): Observable<DwModalResult> {
    const config = new DwModalConfig<TeamMemberPickerData>();
    config.title = 'Add team member'; config.component = TeamMembersComponent;
    config.data = { ...data, members: data.members.slice() }; config.cloneData = false;
    config.modalSize = DwModalSize.responsive;
    config.buttons = [{ ...DwModalButtonStandardConfigs.instance.closeButton }];
    return modal.showModal(config);
}

type Member = ChallengeTeamMemberEntity & Omit<UserProfileEntity, 'UserChallenge'> & { UserPictureMediaId?: number };
@Component({ selector: 'app-team-members', templateUrl: './team-members.component.html', styleUrls: ['./team-members.component.scss'] })
export class TeamMembersComponent implements OnInit, OnDestroy, DwModalHandler {
    modalConfig: DwModalConfig<TeamMemberPickerData>;
    closeModal: EventEmitter<DwModalResult>;
    @Input() members: Member[] = [];
    @Input() summaries: UserFitnessSummaryEntity[] = [];
    @Input() teamId: number;
    @Input() challengeId: number;
    @Input() canEdit = false;
    @Input() pickerOnly = false;
    @Output() membershipChanged = new EventEmitter<CentralTeamChange>();
    adding = false;
    loading = false;
    busy = false;
    search = '';
    error = '';
    message = '';
    candidates: UserChallengeEntity[] = [];
    private subscriptions = new Subscription();
    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService, private modal: DwModalService, private centralData: CentralDataService) {}
    ngOnInit(): void {
        if (!this.modalConfig) { return; }
        const data = this.modalConfig.data;
        Object.assign(this, { pickerOnly: true, canEdit: true, teamId: data.teamId, challengeId: data.challengeId, members: data.members });
        this.openAdd();
    }
    buttonClick(button: DwButtonConfig): Observable<DwModalResult> {
        const result = new DwModalResult(); result.closeModal = !this.busy; result.cancelled = true; result.buttonCommand = button.command;
        return of(result);
    }
    private emitMembershipChange(change: CentralTeamChange): void {
        this.membershipChanged.emit(change);
        this.modalConfig?.data.onChanged(change);
    }
    trackMember(index: number, member: Member): number { return member.UserChallengeId; }
    name(member: Member): string { return member.UserChallengeIdDisplay || 'Streaker'; }
    activityCount(id: number): number | string { const summary = this.summaries.find(row => row.UserChallengeId === id); return summary ? summary.TotalActivities ?? 0 : '—'; }
    profileLink(member: Member): string { return userProfilePath(member.ProfileSlug); }
    candidateName(candidate: UserChallengeEntity): string {
        const person = candidate.UserProfile?.DwUser;
        return [person?.FirstName, person?.LastName].filter(Boolean).join(' ') || 'Streaker';
    }
    get visibleCandidates(): UserChallengeEntity[] {
        const ids = new Set(this.members.map(member => member.UserChallengeId));
        return this.candidates.filter(row => !ids.has(row.UserChallengeId)
            && this.candidateName(row).toLowerCase().includes(this.search.trim().toLowerCase()));
    }
    lastCaptain(member: Member): boolean { return member.IsTeamAdmin && this.members.filter(row => row.IsTeamAdmin).length <= 1; }
    openAdd(): void {
        if (!this.canEdit || this.busy || this.loading || !this.challengeId) { return; }
        if (!this.pickerOnly) {
            this.subscriptions.add(openTeamMemberPicker(this.modal, { teamId: this.teamId, challengeId: this.challengeId, members: this.members,
                onChanged: change => {
                    this.message = ''; this.error = '';
                    this.membershipChanged.emit(change);
                } }).pipe(take(1)).subscribe());
            return;
        }
        this.adding = true; this.loading = true; this.error = ''; this.search = ''; this.candidates = [];
        const profile = AppMetaData.UserProfile.CreateQueryBuilder().addFields(a => [a.UserProfileId, a.UserId, a.ProfileSlug]);
        const query = AppMetaData.UserChallenge.CreateQueryBuilder()
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, this.challengeId))
            .addFilterAnd(f => f.Equal(a => a.IsRegistrationComplete, true))
            .addPrefetch(r => r.UserProfile, profile.query);
        this.subscriptions.add(combineLatest([this.orm.executeQuery<UserChallengeEntity>(query), this.centralData.publicIdentities(this.challengeId)]).pipe(take(1)).subscribe(([rows, identities]) => {
            const candidates = rows.map(row => {
                const identity = identities.find(person => person.UserChallengeId === row.UserChallengeId && person.UserProfileId === row.UserProfileId && (!row.UserProfile?.UserId || person.UserId === row.UserProfile.UserId));
                if (!identity) { return row; }
                const person = AppMetaData.DwUser.CreateEntity(); Object.assign(person, { UserId: identity.UserId, FirstName: identity.FirstName, LastName: identity.LastName, UserPictureMediaId: identity.UserPictureMediaId });
                return { ...row, UserProfile: { ...AppMetaData.UserProfile.CreateEntity(), ...row.UserProfile, ...identity.Appearance, UserProfileId: identity.UserProfileId, UserId: identity.UserId, ProfileSlug: identity.ProfileSlug, DwUser: person } };
            });
            this.candidates = candidates.sort((a, b) => this.candidateName(a).localeCompare(this.candidateName(b))); this.loading = false;
        }, () => { this.loading = false; this.error = 'Unable to load streakers. Try Add members again.'; }));
    }
    add(candidate: UserChallengeEntity): void {
        if (!this.canEdit || this.busy || !this.visibleCandidates.includes(candidate)) { return; }
        const entity = AppMetaData.ChallengeTeamMember.CreateEntity();
        entity.ChallengeTeamId = this.teamId; entity.UserChallengeId = candidate.UserChallengeId; entity.IsTeamAdmin = false;
        this.save(entity, this.candidateName(candidate) + ' added.', candidate);
    }
    promote(member: Member): void {
        if (!this.canEdit || this.busy || member.IsTeamAdmin || !this.members.includes(member)) { return; }
        const entity = AppMetaData.ChallengeTeamMember.CreateEntity();
        entity.ChallengeTeamId = this.teamId; entity.UserChallengeId = member.UserChallengeId; entity.ChallengeTeamMemberId = member.ChallengeTeamMemberId; entity.IsTeamAdmin = true;
        this.save(entity, this.name(member) + ' is now a captain.');
    }
    toggleCaptain(member: Member): void {
        if (!member.IsTeamAdmin) { this.promote(member); return; }
        if (!this.canEdit || this.busy || this.lastCaptain(member) || !this.members.includes(member)) { return; }
        const entity = AppMetaData.ChallengeTeamMember.CreateEntity();
        entity.ChallengeTeamMemberId = member.ChallengeTeamMemberId; entity.IsTeamAdmin = false;
        this.save(entity, this.name(member) + ' is now a member.');
    }
    private save(entity: ChallengeTeamMemberEntity, message: string, candidate?: UserChallengeEntity): void {
        const isNew = !entity.ChallengeTeamMemberId;
        this.busy = true; this.error = ''; this.message = '';
        this.subscriptions.add(this.orm.saveEntity(entity).pipe(take(1)).subscribe((saved: ChallengeTeamMemberEntity) => {
            const member: Member = { ...entity, ...saved, ...(candidate ? { ...candidate.UserProfile, UserChallenge: candidate, UserPictureMediaId: candidate.UserProfile?.DwUser?.UserPictureMediaId, UserChallengeIdDisplay: this.candidateName(candidate) } : {}) };
            this.busy = false; this.message = message;
            if (isNew) { this.members = [...this.members, member]; }
            else if (entity.IsTeamAdmin != null) { this.members = this.members.map(member => member.ChallengeTeamMemberId === entity.ChallengeTeamMemberId ? { ...member, IsTeamAdmin: entity.IsTeamAdmin } : member); }
            this.emitMembershipChange({ teamId: this.teamId, member });
        }, () => { this.busy = false; this.error = 'Unable to confirm the change. Refresh the team before trying again.'; }));
    }
    remove(member: Member): void {
        if (!this.canEdit || this.busy || this.lastCaptain(member) || !this.members.includes(member)) { return; }
        this.busy = true;
        this.subscriptions.add(this.modal.showConfirmDialog({ title: 'Remove member?',
            message: 'Remove ' + this.name(member) + ' from this team?', useConfirmDefaults: true, showConfirmDialog: true
        }).pipe(take(1)).subscribe(confirmed => {
            if (!confirmed) { this.busy = false; return; }
            const entity = AppMetaData.ChallengeTeamMember.CreateEntity(); entity.ChallengeTeamMemberId = member.ChallengeTeamMemberId;
            this.error = ''; this.message = '';
            this.subscriptions.add(this.orm.deleteEntity(entity).pipe(take(1)).subscribe(() => {
                this.busy = false; this.message = this.name(member) + ' removed.'; this.members = this.members.filter(row => row.ChallengeTeamMemberId !== member.ChallengeTeamMemberId); this.membershipChanged.emit({ teamId: this.teamId, member, removed: true });
            }, () => { this.busy = false; this.error = 'Unable to confirm removal. Refresh the team before trying again.'; }));
        }, () => { this.busy = false; }));
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
}
